import { useCallback, useRef, useState } from "react";
import { Button } from "components/button";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet";
import { Badge } from "components/ui/badge";
import { SearchQueryFilters, SearchType } from "hooks/search/types";
import { AgeRangeAvailableBounds } from "reedy-data/api";
import { useFilters } from "hooks/search/useFilters";
import { useSearch } from "hooks/search/useSearch";
import { isTopMatchesSearch } from "hooks/search/utils";
import { formatNumber } from "utils/formatting/formatNumber";
import { defaultFilterValues } from "./constants";
import { FilterItem } from "./FilterItem";
import { isValidFilter } from "./utils";

interface SearchFiltersProps {
  activeFilters: number;
  onApply: () => void;
  onSheetOpen?: () => void;
  appliedFilters: SearchQueryFilters;
  searchQuery?: string;
  searchType?: SearchType;
  searchPageSize?: number | null;
  ageRangeAvailableBounds?: AgeRangeAvailableBounds | null;
}

export function SearchFilters({
  activeFilters,
  onApply,
  onSheetOpen,
  appliedFilters,
  searchQuery,
  searchType,
  searchPageSize,
  ageRangeAvailableBounds,
}: SearchFiltersProps) {
  const { t } = useTranslation(["books", "general"]);
  const { setValue, control } = useFormContext();
  const { filters } = useFilters();

  const currentFilters = useWatch({ control, name: "filters" });
  const [open, setOpen] = useState(false);
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);
  const appliedRef = useRef(false);

  // Live preview of the drawer draft: drives the match count and age-range bounds.
  // A top-matches `found` depends on the page size (work collapse, score cut), so
  // the preview requests the page size of the results, not per_page 0.
  const { data: matchPreview, isPlaceholderData } = useSearch(
    {
      query: searchQuery,
      type: searchType,
      filters: currentFilters,
      pageSize: isTopMatchesSearch(searchType, searchQuery)
        ? searchPageSize
        : 0,
    },
    { enabled: open, debounce: 250, includeAgeRangeBounds: true },
  );

  // Bounds follow the draft, falling back to applied-filter bounds before the
  // first preview. During a refetch the previous response is placeholder data,
  // so the slider locks until fresh bounds arrive.
  const salesDataMaxDate = new Date();
  const ageRangeHasOtherFilters = Object.entries(currentFilters ?? {}).some(
    ([name, filter]) =>
      name !== "ageRange" && filter && isValidFilter(filter, salesDataMaxDate),
  );
  const liveAgeRangeBounds =
    matchPreview?.ageRangeAvailableBounds ?? ageRangeAvailableBounds ?? null;
  const matchingCount = matchPreview?.found;
  const hasNoMatches = matchingCount === 0;

  const toggleFilter = (name: string) => {
    setExpandedFilter((prev) => (prev === name ? null : name));
  };

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen && !appliedRef.current) {
        setValue("filters", { ...defaultFilterValues, ...appliedFilters });
      }
      appliedRef.current = false;
      setOpen(isOpen);
      if (isOpen) onSheetOpen?.();
    },
    [setValue, appliedFilters, onSheetOpen],
  );

  const handleApply = useCallback(() => {
    appliedRef.current = true;
    onApply();
    setOpen(false);
  }, [onApply]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        <Button
          className="h-10"
          variant="tertiary"
          icon="list-filter"
          aria-label={t("books:filters.label")}
        >
          {t("books:filters.label")}
          {activeFilters > 0 && (
            <Badge
              data-testid="active-filters-badge"
              variant="error"
              className="ml-2 px-0 min-w-4 w-4 h-4"
            >
              {activeFilters}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="flex flex-col p-0 gap-0 sm:max-w-md"
      >
        <SheetHeader className="border-b px-6 py-4 mb-0">
          <SheetTitle className="mb-0 text-left">
            {t("books:filters.label")}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t("books:filters.label")}
          </SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto">
          {filters.map(
            (filter) =>
              filter.show && (
                <FilterItem
                  key={filter.name}
                  {...filter}
                  isExpanded={expandedFilter === filter.name}
                  onToggle={() => toggleFilter(filter.name)}
                  searchQuery={searchQuery}
                  searchType={searchType}
                  ageRangeAvailableBounds={liveAgeRangeBounds}
                  ageRangeHasOtherFilters={ageRangeHasOtherFilters}
                  ageRangeBoundsLoading={isPlaceholderData}
                />
              ),
          )}
        </div>
        <div className="flex items-center justify-between border-t px-6 py-4">
          <Button
            variant="tertiary"
            size="small"
            label={t("general:buttons.clearAll")}
            className="rounded-[8px]"
            onClick={() => setValue("filters", defaultFilterValues)}
          />
          <Button
            variant="primary"
            size="small"
            icon="search"
            className="rounded-[8px]"
            onClick={handleApply}
            disabled={hasNoMatches}
          >
            {t("general:buttons.apply")}
            {matchingCount !== undefined && (
              <span
                data-testid="matching-documents-count"
                className="ml-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 text-xs leading-none font-semibold text-white tabular-nums"
              >
                {formatNumber(matchingCount)}
              </span>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
